import React from 'react';
import { RadioButton, useTheme } from '@zydalabs/storefront-components';
import { useRouter } from 'next/router';
import { Typography } from '@zydalabs/zac-react';
import useTranslation from 'next-translate/useTranslation';
import { Setting, Variant } from '../../../../service/types/generated';
import VariantOptionField from '../VariantOptionField';
import { usePersistedCountryDetails } from '../../../../common/hooks';
import { AvailabilityInfo, getAvailabilityTypeAttributes, getPrepTimeText } from '../../utils';

type MenuItemVariantsType = {
  variants: Array<Variant>;
  title: string;
  productPrepTime: number;
  onVariantSelection: (variantId: string) => void;
  selectedVariantId: string;
  isArabic: boolean;
  stockThresholdSettings: Partial<Setting>;
  productFulfillmentMode: string;
  mode: string;
  isNonEditingVariantInCart: (variantId: string) => boolean;
};

const MenuItemVariants: React.FC<MenuItemVariantsType> = ({
  variants,
  title,
  productPrepTime,
  onVariantSelection,
  selectedVariantId,
  isArabic,
  stockThresholdSettings,
  productFulfillmentMode,
  mode,
  isNonEditingVariantInCart,
}) => {
  const { t } = useTranslation('product');
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const router = useRouter();
  const theme = useTheme();
  const lang = router.locale;

  const getAdditionalInfo = (variant: Variant, availabilityInfo: AvailabilityInfo) => {
    const { prepTime } = variant || {};

    if (prepTime > productPrepTime)
      return (
        <Typography variant="body14" mt={1} mb={0} isDisabled testId="custom-prepTime">
          {`${getPrepTimeText(prepTime, lang)}`}
        </Typography>
      );

    return null;
  };

  const getVariantInCartLabel = () => (
    <Typography variant="body14" mt={1} mb={0} isDisabled>
      {t('alreadyInCart')}
    </Typography>
  );

  return (
    <>
      <Typography variant="headingExpressive16" mb={16} mt={16} testId="variants-title">
        {title}
      </Typography>
      <RadioButton
        fields={variants.map(variant => {
          const { id, titleAr, titleEn, price, discountedPrice, discountEnabled, stockData } = variant || {};
          const { unavailable, stockCount, trackingType } = stockData || {};
          const variantAvailabilityInfo = getAvailabilityTypeAttributes({
            stockCount,
            unavailable,
            stockThresholdSettings,
            trackingType,
            selectedMode: mode,
            productAvailabilityMode: productFulfillmentMode,
            theme,
          });
          const additionalInfo = getAdditionalInfo(variant, variantAvailabilityInfo);
          const { unavailabilityReason } = variantAvailabilityInfo;
          const alreadyInCart = isNonEditingVariantInCart(id);

          return {
            content: (
              <VariantOptionField
                key={id}
                fieldTitle={isArabic ? titleAr : titleEn}
                additionalInfo={
                  <>
                    {additionalInfo}
                    {alreadyInCart && getVariantInCartLabel()}
                  </>
                }
                isDisabled={!!unavailabilityReason || alreadyInCart}
                price={price}
                discountedPrice={discountEnabled && discountedPrice}
                currency={persistedCountryDetails?.currency}
              />
            ),
            fieldValue: id,
            disabled: !!unavailabilityReason || alreadyInCart,
          };
        })}
        onChange={onVariantSelection}
        value={selectedVariantId}
        name="variants"
        mt={2}
      />
    </>
  );
};

export default MenuItemVariants;
